import { useForm } from 'react-hook-form';
import { useAppSelector } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import {
  SettingsDescription,
  SettingsMain,
  SettingsPageHeader,
  SettingsSectionTitle,
} from '../SettingsLayout';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import { CommerceType } from 'types/models/company';
import Form from 'storybookConfig/stories/organisms/Form';
import FormField from 'storybookConfig/stories/cells/FormField';
import FileUpload from 'storybookConfig/stories/cells/FileUpload';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';
import Icon from 'storybookConfig/stories/molecules/Icon';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import { uploadPublicImage } from 'utils/api/documents';
import type { Company } from 'types/models/company';
import type { UploadedFile } from 'storybookConfig/stories/cells/FileUpload';
import { useState, useEffect } from 'react';

interface FormFields {
  brandProfilePhoto: string;
  brandHeaderPhoto: string;
  brandCardPhoto: string;
}

const Assets = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const [brandProfilePhoto, setBrandProfilePhoto] = useState<string | null>(
    company?.marketplaceSettings?.brandProfilePhoto || null
  );
  const [brandHeaderPhoto, setBrandHeaderPhoto] = useState<string | null>(
    company?.marketplaceSettings?.brandHeaderPhoto || null
  );
  const [brandCardPhoto, setBrandCardPhoto] = useState<string | null>(
    company?.marketplaceSettings?.brandCardPhoto || null
  );

  const { handleSubmit, formState, register } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      brandProfilePhoto: company?.marketplaceSettings?.brandProfilePhoto || '',
      brandHeaderPhoto: company?.marketplaceSettings?.brandHeaderPhoto || '',
      brandCardPhoto: company?.marketplaceSettings?.brandCardPhoto || '',
    },
  });

  // Register custom file upload fields once using useEffect
  useEffect(() => {
    register('brandProfilePhoto');
    register('brandHeaderPhoto');
    register('brandCardPhoto');
  }, [register]);

  const handleImageSelect = (field: keyof FormFields) => async (uploadedFile: UploadedFile) => {
    if (!uploadedFile.file) return;

    try {
      const file = new File([uploadedFile.file], uploadedFile.fileName, {
        type: uploadedFile.fileType,
      });

      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        addErrorAlert('File too large', 'Please select an image smaller than 5MB');
        return;
      }

      await uploadPublicImage(file, {
        onPreview: (url) => {
          if (field === 'brandProfilePhoto') {
            setBrandProfilePhoto(url);
          } else if (field === 'brandHeaderPhoto') {
            setBrandHeaderPhoto(url);
          } else if (field === 'brandCardPhoto') {
            setBrandCardPhoto(url);
          }
        },
        onComplete: (url) => {
          if (field === 'brandProfilePhoto') {
            setBrandProfilePhoto(url);
          } else if (field === 'brandHeaderPhoto') {
            setBrandHeaderPhoto(url);
          } else if (field === 'brandCardPhoto') {
            setBrandCardPhoto(url);
          }
        },
        onError: (error) => {
          console.error('Error uploading image', error);
          addErrorAlert('Something went wrong', 'Unable to upload image');
        },
      });
    } catch (error) {
      console.error('Error uploading image', error);
      addErrorAlert('Something went wrong', 'Unable to upload image');
    }
  };

  const { mutate: updateAssets, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: async () => {
      const updatedData = {
        brandProfilePhoto: brandProfilePhoto || '',
        brandHeaderPhoto: brandHeaderPhoto || '',
        brandCardPhoto: brandCardPhoto || '',
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: () => {
      addSuccessAlert('Assets Updated', 'Your brand assets have been saved successfully.');
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update assets', error);
    },
  });

  const renderUploadContent = (label: string) => (
    <Flex gap="16px" alignItemsCenter justifyCenter>
      <Icon name="file-up" size="48px" color="iconDefault" />
      <Flex.Item grow={1}>
        <Body color="bodyTextSecondary" variant="Body/Regular">
          Drag and drop or <strong>click here to {label}</strong>
        </Body>
      </Flex.Item>
    </Flex>
  );

  const onSubmit = (data: FormFields) => {
    updateAssets(data);
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Assets">
        <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
      </SettingsPageHeader>
      <SettingsSectionTitle>Brand Assets</SettingsSectionTitle>
      <SettingsDescription>
        Upload your brand assets to showcase your brand in the marketplace.
      </SettingsDescription>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px' }}>
        <Form.FieldGroup>
          <FormField
            labelText="Brand Profile Photo"
            id="brandProfilePhoto"
            errorText={formState.errors.brandProfilePhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandProfilePhoto')}
              borderRadius="8px"
              previewUrl={brandProfilePhoto || undefined}
              id="brandProfilePhotoUpload"
            >
              {renderUploadContent('upload profile photo')}
            </FileUpload>
          </FormField>

          <FormField
            labelText="Company Header Image"
            id="brandHeaderPhoto"
            errorText={formState.errors.brandHeaderPhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandHeaderPhoto')}
              borderRadius="8px"
              previewUrl={brandHeaderPhoto || undefined}
              id="brandHeaderPhotoUpload"
            >
              {renderUploadContent('upload header image')}
            </FileUpload>
          </FormField>

          <FormField
            labelText="Brand Card Image"
            id="brandCardPhoto"
            errorText={formState.errors.brandCardPhoto?.message}
          >
            <FileUpload
              onUpload={handleImageSelect('brandCardPhoto')}
              borderRadius="8px"
              previewUrl={brandCardPhoto || undefined}
              id="brandCardPhotoUpload"
            >
              {renderUploadContent('upload card image')}
            </FileUpload>
          </FormField>
        </Form.FieldGroup>
        <Form.Footer justifyEnd>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Form.Footer>
      </Form>
    </SettingsMain>
  );
};

export default Assets;
